import type { NavigationItem } from '@edsis/navigation';
import type { ApplicationProfileName } from '@edsis/angular/platform/mfe';

export const MFE_DEPLOYMENT_STAGES = ['development', 'staging', 'production'] as const;

export type MfeDeploymentStage = (typeof MFE_DEPLOYMENT_STAGES)[number];
export type MfeGatewayStage = 'local' | MfeDeploymentStage;
export type MfeMountPath = `/${string}`;
export type MfeExposure = `./${string}`;
export type HttpsOrigin = `https://${string}`;

export interface FirebaseHostingDeployment {
  readonly provider: 'firebase';
  readonly project: string;
  readonly target: string;
  readonly site: string;
  readonly automation: 'push' | 'manual';
}

export interface ExternalHostingDeployment {
  readonly provider: 'external';
}

export type HostingDeployment = FirebaseHostingDeployment | ExternalHostingDeployment;

export interface AppEnvironmentRegistration {
  readonly origin: HttpsOrigin;
  readonly hosting: HostingDeployment;
}

export interface RegisteredWorkspaceApp {
  readonly id: string;
  readonly project: string;
  readonly devPort: number;
}

export interface HostRegistration extends RegisteredWorkspaceApp {
  readonly environments: Readonly<Record<MfeDeploymentStage, AppEnvironmentRegistration>>;
}

export interface MfeContractRegistration {
  readonly major: number;
  readonly exposure: MfeExposure;
}

export interface MfeStandaloneAuthRegistration {
  readonly authenticatedHomePath: MfeMountPath;
  readonly oauthCallbackPath: MfeMountPath;
  readonly postLoginReturnPath: MfeMountPath;
}

export interface MfeRegistration extends RegisteredWorkspaceApp {
  readonly displayName: string;
  readonly hostComposition: 'standard' | 'custom';
  readonly mountPath: MfeMountPath;
  readonly contract: MfeContractRegistration;
  readonly standaloneProfile: ApplicationProfileName;
  readonly auth: MfeStandaloneAuthRegistration;
  /** Root-relative links are rebased onto mountPath by the host adapter. */
  readonly fallbackNavigation: readonly NavigationItem[];
  /**
   * A remote may start as local-only. Once an environment is declared here,
   * the shell includes it in that environment's federation manifest and CSP.
   */
  readonly environments: Readonly<Partial<Record<MfeDeploymentStage, AppEnvironmentRegistration>>>;
}

export interface MfeWorkspaceRegistry {
  readonly stages: Readonly<Record<MfeDeploymentStage, { readonly branch: string }>>;
  readonly host: HostRegistration;
  readonly remotes: readonly MfeRegistration[];
}

export interface RuntimeRemoteConfig {
  readonly remoteEntry: string;
}

export type RuntimeRemoteRegistry = Readonly<Record<string, RuntimeRemoteConfig>>;

export interface MfeGatewayReturnTarget {
  readonly mfeId: string;
  readonly returnUrl: string;
}

export interface MfeGatewayEnvironmentVariables {
  readonly AUTH_SERVICE_GOOGLE_GATEWAY_CLIENTS: string;
  readonly CORS_ALLOWED_ORIGINS: string;
  /** Cookie refresh/logout provenance follows the identical exact browser-origin policy. */
  readonly AUTH_SERVICE_COOKIE_MUTATION_ALLOWED_ORIGINS: string;
}

export function defineMfeRegistry<const TRegistry extends MfeWorkspaceRegistry>(registry: TRegistry): TRegistry {
  return registry;
}

export const mfeRegistry = defineMfeRegistry({
  stages: {
    development: { branch: 'development' },
    staging: { branch: 'staging' },
    production: { branch: 'main' },
  },
  host: {
    id: 'shell',
    project: 'erp',
    devPort: 3705,
    environments: {
      development: {
        origin: 'https://edsis.dev',
        hosting: {
          provider: 'firebase',
          project: 'dev-etos-dsis',
          target: 'erp-dev',
          site: 'dev-etos-dsis',
          automation: 'push',
        },
      },
      staging: {
        origin: 'https://stg.edsis.dev',
        hosting: {
          provider: 'firebase',
          project: 'stg-etos-dsis',
          target: 'erp-staging',
          site: 'stg-etos-dsis',
          automation: 'manual',
        },
      },
      production: {
        origin: 'https://edsis.app',
        hosting: {
          provider: 'firebase',
          project: 'etos-dsis',
          target: 'erp-prod',
          site: 'etos-dsis',
          automation: 'push',
        },
      },
    },
  },
  remotes: [
    // MFE_REGISTRY_ENTRIES_START — generator inserts new registrations below this line.
    {
      id: 'developer',
      displayName: 'Technical Documentation & Guide',
      hostComposition: 'custom',
      project: 'developer',
      devPort: 4501,
      mountPath: '/developer',
      contract: {
        major: 1,
        exposure: './Mfe',
      },
      standaloneProfile: 'mfe-standalone',
      auth: {
        authenticatedHomePath: '/overview',
        oauthCallbackPath: '/auth/google/callback',
        postLoginReturnPath: '/overview',
      },
      fallbackNavigation: [
        {
          id: 'developer-overview-fallback',
          title: 'Overview',
          type: 'basic',
          icon: 'article',
          link: '/overview',
        },
      ],
      environments: {
        development: {
          origin: 'https://developer-guide-edsis-dev.web.app',
          hosting: {
            provider: 'firebase',
            project: 'dev-etos-dsis',
            target: 'developer-guide-dev',
            site: 'developer-guide-edsis-dev',
            automation: 'push',
          },
        },
        staging: {
          origin: 'https://developer-edsis-stg.web.app',
          hosting: { provider: 'external' },
        },
        production: {
          origin: 'https://developer-edsis.web.app',
          hosting: { provider: 'external' },
        },
      },
    },
    // MFE_REGISTRY_ENTRIES_END
  ],
});

/** Widened view keeps consumers stable when generated entries have sparse environment maps. */
export const registeredMfes: readonly MfeRegistration[] = mfeRegistry.remotes;
export const registeredApps: readonly RegisteredWorkspaceApp[] = [mfeRegistry.host, ...registeredMfes];

export function requireMfe(id: string): MfeRegistration {
  const registration = registeredMfes.find((remote) => remote.id === id);

  if (!registration) {
    throw new Error(`Unknown registered MFE "${id}".`);
  }

  return registration;
}

export function getMfeById(id: string): MfeRegistration | undefined {
  return registeredMfes.find((remote) => remote.id === id);
}

export function getDevBaseUrl(app: RegisteredWorkspaceApp): string {
  return `http://localhost:${app.devPort}`;
}

export function getMfeProxyPath(remote: Pick<MfeRegistration, 'id'>): `/_edsis_mfe/${string}` {
  return `/_edsis_mfe/${remote.id}`;
}

export function getMfeRoutePath(remote: Pick<MfeRegistration, 'mountPath'>): string {
  return remote.mountPath.slice(1);
}

export function getLocalRemoteEntryUrl(origin: string, remote: MfeRegistration): string {
  const appOrigin = new URL(origin).origin;
  return `${appOrigin}${getMfeProxyPath(remote)}/remoteEntry.json`;
}

export function getRemoteEntryUrl(remote: MfeRegistration, stage: MfeDeploymentStage): string {
  const environment = remote.environments[stage];

  if (!environment) {
    throw new Error(`MFE "${remote.id}" is not registered for the ${stage} environment.`);
  }

  return `${environment.origin}/remoteEntry.json`;
}

export function getLocalRemoteEntries(origin: string): RuntimeRemoteRegistry {
  return Object.fromEntries(
    registeredMfes.map((remote) => [remote.id, { remoteEntry: getLocalRemoteEntryUrl(origin, remote) }]),
  );
}

export function getRemoteEntriesForEnvironment(stage: MfeDeploymentStage): RuntimeRemoteRegistry {
  return Object.fromEntries(
    registeredMfes.flatMap((remote) => {
      const environment = remote.environments[stage];
      return environment ? [[remote.id, { remoteEntry: `${environment.origin}/remoteEntry.json` }]] : [];
    }),
  );
}

/**
 * Derives the exact OAuth return targets accepted by the shared auth gateway.
 * The gateway consumes these as `mfeId|returnUrl`; no MFE owns Google secrets.
 */
export function getMfeGatewayReturnTargets(
  stage: MfeGatewayStage,
  registry: MfeWorkspaceRegistry = mfeRegistry,
): readonly MfeGatewayReturnTarget[] {
  return registry.remotes
    .flatMap((remote) => {
      const origin = stage === 'local' ? getDevBaseUrl(remote) : remote.environments[stage]?.origin;

      return origin
        ? [
            {
              mfeId: remote.id,
              returnUrl: `${origin}${remote.auth.oauthCallbackPath}`,
            },
          ]
        : [];
    })
    .sort((left, right) => left.mfeId.localeCompare(right.mfeId));
}

export function formatMfeGatewayReturnAllowlist(
  stage: MfeGatewayStage,
  registry: MfeWorkspaceRegistry = mfeRegistry,
): string {
  return getMfeGatewayReturnTargets(stage, registry)
    .map(({ mfeId, returnUrl }) => `${mfeId}|${returnUrl}`)
    .join(',');
}

/**
 * Derives the exact browser origins accepted by the shared API gateway.
 * The shell is always included; only MFEs deployed in the requested stage are
 * included outside local development.
 */
export function getCorsAllowedOrigins(
  stage: MfeGatewayStage,
  registry: MfeWorkspaceRegistry = mfeRegistry,
): readonly string[] {
  const hostOrigin = stage === 'local' ? getDevBaseUrl(registry.host) : registry.host.environments[stage].origin;
  const remoteOrigins = registry.remotes
    .flatMap((remote) => {
      const origin = stage === 'local' ? getDevBaseUrl(remote) : remote.environments[stage]?.origin;
      return origin ? [origin] : [];
    })
    .sort((left, right) => left.localeCompare(right));

  return [hostOrigin, ...new Set(remoteOrigins.filter((origin) => origin !== hostOrigin))];
}

export function formatCorsAllowedOrigins(stage: MfeGatewayStage, registry: MfeWorkspaceRegistry = mfeRegistry): string {
  return getCorsAllowedOrigins(stage, registry).join(',');
}

/** Produces the three backend values that must stay aligned with this registry. */
export function getMfeGatewayEnvironmentVariables(
  stage: MfeGatewayStage,
  registry: MfeWorkspaceRegistry = mfeRegistry,
): MfeGatewayEnvironmentVariables {
  return {
    AUTH_SERVICE_GOOGLE_GATEWAY_CLIENTS: formatMfeGatewayReturnAllowlist(stage, registry),
    CORS_ALLOWED_ORIGINS: formatCorsAllowedOrigins(stage, registry),
    AUTH_SERVICE_COOKIE_MUTATION_ALLOWED_ORIGINS: formatCorsAllowedOrigins(stage, registry),
  };
}

export function requireFirebaseHosting(environment: AppEnvironmentRegistration): FirebaseHostingDeployment {
  if (environment.hosting.provider !== 'firebase') {
    throw new Error(`Expected Firebase hosting for ${environment.origin}.`);
  }

  return environment.hosting;
}

export function validateMfeRegistry(registry: MfeWorkspaceRegistry = mfeRegistry): readonly string[] {
  const errors: string[] = [];
  const ids = new Set<string>();
  const projects = new Set<string>();
  const ports = new Set<number>();
  const mountPaths = new Set<string>();
  const firebaseTargets = new Set<string>();
  const firebaseSites = new Set<string>();

  for (const [kind, app] of [
    ['host', registry.host],
    ...registry.remotes.map((remote) => ['remote', remote] as const),
  ] as const) {
    validateSlug(`${kind} id`, app.id, errors);
    validateSlug(`${app.id} Angular project`, app.project, errors);
    addUnique('application id', app.id, ids, errors);
    addUnique('Angular project', app.project, projects, errors);

    if (!Number.isInteger(app.devPort) || app.devPort < 1024 || app.devPort > 65_535) {
      errors.push(`${app.id}: devPort must be an integer between 1024 and 65535.`);
    } else {
      addUnique('development port', app.devPort, ports, errors);
    }
  }

  for (const remote of registry.remotes) {
    if (!remote.displayName.trim()) {
      errors.push(`${remote.id}: displayName cannot be empty.`);
    }

    if (remote.hostComposition !== 'standard' && remote.hostComposition !== 'custom') {
      errors.push(`${remote.id}: hostComposition must be "standard" or "custom".`);
    }

    if (!/^\/[a-z0-9]+(?:[/-][a-z0-9]+)*$/.test(remote.mountPath) || remote.mountPath.endsWith('/')) {
      errors.push(`${remote.id}: mountPath must be a normalized absolute application path.`);
    } else {
      addUnique('mount path', remote.mountPath, mountPaths, errors);
    }

    if (!Number.isInteger(remote.contract.major) || remote.contract.major < 1) {
      errors.push(`${remote.id}: contract.major must be a positive integer.`);
    }

    if (!/^\.\/[A-Z][A-Za-z0-9]*$/.test(remote.contract.exposure)) {
      errors.push(`${remote.id}: contract.exposure must look like "./Mfe".`);
    }

    if (!/^[a-z][a-z0-9-]*$/.test(remote.standaloneProfile)) {
      errors.push(`${remote.id}: standaloneProfile must use lowercase kebab-case.`);
    }

    for (const [field, path] of Object.entries(remote.auth)) {
      if (!isNormalizedAbsolutePath(path)) {
        errors.push(`${remote.id}: auth.${field} must be a normalized absolute path.`);
      }
    }
  }

  for (const stage of MFE_DEPLOYMENT_STAGES) {
    if (!registry.stages[stage].branch.trim()) {
      errors.push(`${stage}: deployment branch cannot be empty.`);
    }

    validateEnvironment(
      registry.host.id,
      stage,
      registry.host.environments[stage],
      errors,
      firebaseTargets,
      firebaseSites,
    );

    for (const remote of registry.remotes) {
      const environment = remote.environments[stage];
      if (environment) {
        validateEnvironment(remote.id, stage, environment, errors, firebaseTargets, firebaseSites);
      }
    }
  }

  return errors;
}

function validateEnvironment(
  appId: string,
  stage: MfeDeploymentStage,
  environment: AppEnvironmentRegistration,
  errors: string[],
  firebaseTargets: Set<string>,
  firebaseSites: Set<string>,
): void {
  try {
    const url = new URL(environment.origin);
    if (url.protocol !== 'https:' || url.origin !== environment.origin) {
      errors.push(`${appId}/${stage}: origin must be a normalized HTTPS origin.`);
    }
  } catch {
    errors.push(`${appId}/${stage}: origin is not a valid URL.`);
  }

  if (environment.hosting.provider === 'firebase') {
    const hosting = environment.hosting;
    for (const [field, value] of [
      ['project', hosting.project],
      ['target', hosting.target],
      ['site', hosting.site],
    ] as const) {
      if (!value.trim()) {
        errors.push(`${appId}/${stage}: Firebase ${field} cannot be empty.`);
      }
    }

    addUnique('Firebase target', hosting.target, firebaseTargets, errors);
    addUnique('Firebase site', hosting.site, firebaseSites, errors);
  }
}

function validateSlug(label: string, value: string, errors: string[]): void {
  if (!/^[a-z][a-z0-9-]*$/.test(value)) {
    errors.push(`${label} must use lowercase kebab-case.`);
  }

  if (label.endsWith(' id') && value.length > 64) {
    errors.push(`${label} cannot exceed 64 characters.`);
  }
}

function isNormalizedAbsolutePath(path: string): boolean {
  return /^\/[a-z0-9]+(?:[/-][a-z0-9]+)*$/.test(path) && !path.endsWith('/');
}

function addUnique<T>(label: string, value: T, seen: Set<T>, errors: string[]): void {
  if (seen.has(value)) {
    errors.push(`Duplicate ${label}: ${String(value)}.`);
    return;
  }

  seen.add(value);
}
